<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useMenuStore } from '@/stores/menuStore';
import { storeToRefs } from 'pinia';

import IlluminatedButton from '@/components/buttons/IlluminatedButton.vue';
import { gsap } from 'gsap';
import SplitText from 'gsap/SplitText';

gsap.registerPlugin(SplitText);

const menuStore = useMenuStore();
const { items: menuItems, footerItems } = storeToRefs(menuStore);

const { fetchMenus } = menuStore;

const preFooterRef = ref(null);
const preFooterTitleRef = ref(null);
const preFooterPRef = ref(null);
let titleSplitText;
let pSplitText;

const textBlurEffect = {
    intensity: 25,
    distance: 150,
    enabled: true
};

let isMobile = ref(false);

const applyBlurEffect = (mouseX, mouseY, rect, splitInstance) => {
    if (!splitInstance) return;

    splitInstance.chars.forEach(char => {
        const charRect = char.getBoundingClientRect();
        const charCenterX = charRect.left - rect.left + charRect.width / 2;
        const charCenterY = charRect.top - rect.top + charRect.height / 2;

        const deltaX = mouseX - charCenterX;
        const deltaY = mouseY - charCenterY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        const blurValue = Math.max(0, textBlurEffect.intensity * (1 - distance / textBlurEffect.distance));
        gsap.to(char, { filter: `blur(${blurValue}px)`, duration: 0.3 });
    });
};

const handleMouseMove = (event) => {
    if (isMobile.value) return;

    const preFooter = preFooterRef.value;
    if (!preFooter) return;

    const rect = preFooter.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const images = preFooter.querySelectorAll('.background-image');
    images.forEach(image => {
        const strength = parseFloat(image.dataset.repelStrength) || 0.2;
        const imageRect = image.getBoundingClientRect();
        const imageCenterX = imageRect.left - rect.left + imageRect.width / 2;
        const imageCenterY = imageRect.top - rect.top + imageRect.height / 2;

        const deltaX = mouseX - imageCenterX;
        const deltaY = mouseY - imageCenterY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        const maxDistance = 400;
        const influence = Math.max(0, 1 - distance / maxDistance);

        const repelX = -deltaX * influence * strength;
        const repelY = -deltaY * influence * strength;

        gsap.to(image, { x: repelX, y: repelY, duration: 0.3 });
    });

    if (textBlurEffect.enabled) {
        applyBlurEffect(mouseX, mouseY, rect, titleSplitText);
        applyBlurEffect(mouseX, mouseY, rect, pSplitText);
    }
};

const handleMouseLeave = () => {
    if (isMobile.value) return;

    const preFooter = preFooterRef.value;
    if (!preFooter) return;

    const images = preFooter.querySelectorAll('.background-image');
    gsap.to(images, { x: 0, y: 0, duration: 0.3 });

    if (titleSplitText) {
        gsap.to(titleSplitText.chars, { filter: 'blur(0px)', duration: 0.3, overwrite: true });
    }
    if (pSplitText) {
        gsap.to(pSplitText.chars, { filter: 'blur(0px)', duration: 0.3, overwrite: true });
    }
};

const handleResize = async () => {
    await document.fonts.ready;
    isMobile.value = window.innerWidth < 767;
    if (isMobile.value) {
        if (titleSplitText) {
            titleSplitText.revert();
            titleSplitText = null;
        }
        if (pSplitText) {
            pSplitText.revert();
            pSplitText = null;
        }
        if (preFooterRef.value) {
            const images = preFooterRef.value.querySelectorAll('.background-image');
            gsap.to(images, { x: 0, y: 0 });
        }
    } else {
        if (!titleSplitText && preFooterTitleRef.value) {
            titleSplitText = new SplitText(preFooterTitleRef.value, { type: 'words,chars' });
        }
        if (!pSplitText && preFooterPRef.value) {
            pSplitText = new SplitText(preFooterPRef.value, { type: 'words,chars' });
        }
    }
};

onMounted(async () => {
    await document.fonts.ready;

    isMobile.value = window.innerWidth < 767;
    handleResize();
    
    window.addEventListener('resize', handleResize);
});

</script>

<template>
    <div class="pre-footer" ref="preFooterRef">
        <p ref="preFooterPRef">Découvrez les possibilités</p>
        <h2 ref="preFooterTitleRef">Testez Orion par vous même</h2>
        <IlluminatedButton
            class="demo-btn"
            label="Let’s go"
            link="/contact#formulaire"
        />

        <div class="background">
            <div class="background-image background-image--1" style="background-image: url('/images/footer/stats-1.webp');" data-repel-strength="0.1"></div>
            <div class="background-image background-image--2" style="background-image: url('/images/footer/stats-2.webp');" data-repel-strength="0.1"></div>
            <div class="background-image background-image--3" style="background-image: url('/images/footer/stats-3.webp');" data-repel-strength="1"></div>
            <div class="background-image background-image--4" style="background-image: url('/images/footer/stats-4.webp');" data-repel-strength="0.1"></div>
            <div class="background-image background-image--5" style="background-image: url('/images/footer/stats-5.webp');" data-repel-strength="1"></div>
        </div>
    </div>

    <footer class="site-footer">

         <NuxtLink to="/"> 
            <NuxtImg 
                src="/images/logo.svg" 
                alt="Logo" 
                class="logo" 
            />
        </NuxtLink>
        
        <nav>
            <NuxtLink v-for="item in menuItems" :key="item.id" :to="item.path">{{ item.title }}</NuxtLink>
        </nav>
       
    </footer>
    <div class="copyright">
        <div class="link-container">
            <span>©2025 Orion by Fusion. Tous droits réservés</span>
            <NuxtLink v-if="footerItems && footerItems.length > 0" v-for="item in footerItems" :key="item.id" :to="item.path" :class="item.classes" :aria-label="item.classes.includes('logo-only')? 'Visitez notre page ' + item.title : ''">{{ !(item.classes.includes('logo-only')) ? item.title : '' }}</NuxtLink>
        </div>
       <p class="footer-creation"></p>
    </div>

    <div class="sub-footer" v-if="isMobile">
       <nav class="call-to-actions">
         
            <NuxtLink to="/contact" class="demo">Demander une Démo</NuxtLink>
            
        </nav>

    </div>

</template>


<style lang="scss" scoped>
.pre-footer {
    border-radius: 25px;
   
    padding: 118px 25px 230px;
    display: flex;
    flex-flow: column;
    gap: 10px;
    justify-content: center;
    align-items: center;
    margin: 150px auto 0;
    text-align: center;
    max-width: 1400px;
    position: relative;
    overflow: hidden;
    z-index: 2;

    &:before{
        content: "";
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: linear-gradient(229deg, #0A1627 26.91%, #143971 77.77%);
        z-index: -1;
        opacity: .75;
    }

    p {
       margin: 0;
       text-align: center;
        font-family: $tertiary-font-family;
        font-size: $upper-title-size;
        line-height: $upper-title-line-height;
        color: $text-color--blue;
    }

    h2 {
        max-width: 540px;
        color: $text-color;
        background: unset;
        background-clip: unset;
        -webkit-background-clip: unset;
        -webkit-text-fill-color: unset;
    }

    .demo-btn {
        margin-top: 20px;
        z-index: 4;
    }

    .background-image{
        position: absolute;
        background-size: contain;
        background-repeat: no-repeat;
        transition: transform 0.3s ease-out;

        &--1,
        &--4{
            display: none;
        }

        &--2{
            bottom: 19px;
            height: 200px;
            aspect-ratio: 161 / 99;
            left: calc(50% - (322px / 2));
        }

        &--3{
           left: 0;
            bottom: 8px;
            height: 175px;
            aspect-ratio: 169 / 175;
        }

        &--4{
            height: 305px;
            flex-shrink: 0;
            aspect-ratio: 319 / 257;
            top: 25%;
            left: 0;
        }

        &--5{
           left: unset;
            right: 0;
            bottom: 0;
            width: 200px;
            aspect-ratio: 59/54;
        }
    }
}

.site-footer{
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 60px;
    padding: 210px 0 85px;
    position: relative;
    overflow: clip;
    background: linear-gradient(00deg, #000000 75%, rgba(0, 0, 0, 0) 100%);

    &::before{
        content: "";
        position: absolute;
        width: 100vw;
        height: 100%;
        top: 250px;
        left: 0vw;
        background-image: url(/images/footer.webp);
        background-size: contain;
        background-repeat: no-repeat;
        z-index: 1;
    }

    &:after{
        content: "";
        position: absolute;
        width: 74vw;
        height: 40%;
        top: 181px;
        left: 12vw;
        z-index: 0;
        border-radius: 50%;
        opacity: 0.3;
        background: rgba(48, 131, 255, 1);
        mix-blend-mode: plus-lighter;
        filter: blur(100px);
    }

    & .logo{
        width: 100%;
        max-width: 315px;
        position: relative;
        z-index: 3;
    }

    & nav{
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 35px;
        position: relative;
        z-index: 3;
       
        & a{
             color: $white;
            font-size: $link-size;
            font-weight: $link-weight;
            text-transform: $link-transform;
            text-decoration: unset;
        }
    }
}

.copyright {
    text-align: center;
    width: 100%;
    background-color: $background-color--lighter;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 15px 30px;
    
    & .link-container {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        align-items: center;
        gap: 15px;
        margin-bottom: 10px;

        & > *:not(:last-child) {
            position: relative;

            &::after {
                content: "";
                position: absolute;
                width: 5px;
                height: 1px;
                background: #6180B7;
                top: 50%;
                right: -10px;
                transform: translate(0, -50%);
            }
        }

        & a {
            &:hover {
                color: $accent-color;
            }

            &.logo-only {
                display: inline-block;
                width: 16px;
                height: 16px;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;

                &.youtube {
                    width: 80px;
                    background-image: url('/images/logo-youtube.svg');
                }
                &.linkedin {
                    width: 20px;
                    background-image: url('/images/logo-linkedin.svg');
                }
            }
            
        }
    }
    & p,
    & span,
    & a {
        color: $accent-color--muted;
        font-size: 12px;
        font-weight: 400;
        line-height: 2.3em;
        margin: 0;

        & a{
            color: $accent-color--muted;
            &:hover{
                color: $accent-color;
            }
        }
    }

    .footer-creation{
        opacity: 0;
        transition: opacity 1s ease;
    }

    &:hover .footer-creation{
            opacity: 1;
    }
}

.sub-footer{
    padding: 29px 24px 15px;

    & .call-to-actions{
        
        & a{
            color: $white;
            font-size: 12px;
            text-transform: $link-transform;
            text-decoration: unset;
            padding: 11px 30px;
            border-radius: 25px;
            color: $accent-color;
            width: 100%;
            display: block;
            text-align: center;
            color: $white;
            font-weight: bold;
            background: $linear-gradient;
        }
    }
}

// responsive
@media (min-width: 767px) {

    .site-footer{
        padding: 520px 0 210px;
    }

    .copyright {
        justify-content: space-between;
        flex-direction: row;
    }

    .pre-footer {
        padding-bottom: 118px;
        
        p {
            font-size: 24px;
        }


        .background-image{
            position: absolute;
            background-size: contain;
            background-repeat: no-repeat;
            transition: transform 0.3s ease-out;

            &--1{
                display: block;
                width: 236px;
                aspect-ratio: 145 / 144;
                z-index: 5;
                top: 2%;
                left: 0%;
            }

            &--2{
                height: 300px;
                top: 20%;
                bottom: unset;
                left: unset;
                right: -168px;
            }

            &--3{
                top: 40%;
                right: -100px;
                bottom: unset;
                left: unset;
                width: 245px;
                height: unset;
            }

            &--4{
                display: block;
                height: 305px;
                flex-shrink: 0;
                aspect-ratio: 319 / 257;
                top: 25%;
                left: 0;
            }

            &--5{
                width: 245px;
                flex-shrink: 0;
                aspect-ratio: 232 / 231;
                bottom: 4%;
                left: 12%;
            }
        }
    }
        
}
</style>
